import { z } from "zod";
import {
  ApplicationActionNameSchema,
  ApplicationDetailSchema,
  ApplicationIconKeySchema,
  ApplicationSummarySchema,
  JsonValueSchema,
} from "../contracts/applications.js";

export const INTERNAL_APPLICATION_CSP = `${[
  "default-src 'none'",
  "script-src 'unsafe-inline'",
  "style-src 'unsafe-inline'",
  "img-src data: blob:",
  "font-src data:",
  "connect-src 'none'",
  "form-action 'none'",
  "frame-src 'none'",
  "object-src 'none'",
  "base-uri 'none'",
].join("; ")};`;
export const INTERNAL_APPLICATION_SANDBOX = "allow-scripts";
export const EXTERNAL_APPLICATION_SANDBOX =
  "allow-same-origin allow-scripts allow-forms allow-popups allow-downloads";
export const EXTERNAL_APPLICATION_REFERRER_POLICY = "no-referrer";
export const APPLICATION_ARGUMENT_MAX_BYTES = 262_144;
export const APPLICATION_DOCUMENT_READY_TIMEOUT_MS = 250;
export const APPLICATION_DOCUMENT_PROOF_TIMEOUT_MS = 250;
export const APPLICATION_SOCKET_PATH = "/api/applications/socket";

const ExternalApplicationUrlSchema = z
  .string()
  .url()
  .refine((value) => {
    try {
      const url = new URL(value);
      return url.protocol === "https:" && !url.username && !url.password;
    } catch {
      return false;
    }
  }, "External application URLs must use HTTPS without credentials");

const INTERNAL_APPLICATION_BRIDGE_BOOTSTRAP = `<script>(()=>{const documentId="document-"+Date.now()+"-"+Math.random().toString(36).slice(2);let accepted=false;let pendingPort;let timer;const announce=()=>{if(accepted)return;pendingPort?.close();const channel=new MessageChannel();pendingPort=channel.port1;channel.port1.onmessage=(event)=>{const message=event.data;if(message?.type==="app.document.ack"){accepted=true;clearInterval(timer);timer=undefined;return}if(message?.type==="app.document.release"){accepted=false;channel.port1.close();if(pendingPort===channel.port1)pendingPort=undefined;start();return}if(message?.type==="app.document.challenge"&&typeof message.nonce==="string"){channel.port1.postMessage({type:"app.document.proof",nonce:message.nonce})}};parent.postMessage({type:"app.document.ready",documentId},"*",[channel.port2])};const start=()=>{if(accepted||timer!==undefined)return;announce();timer=setInterval(announce,50)};start();addEventListener("pagehide",()=>clearInterval(timer),{once:true})})()</script>`;

// Only `id` is a UUID (the instance store mints it with `randomUUID`, and the
// `/applications/external/:id` route matches on that shape). `orgId` is the
// configured instance org id — any non-empty string, `"instance"` by default —
// and `createdByUserId` is a better-auth user id, which is not a UUID and is
// null on a row whose creator the store never recorded. Demanding UUIDs of
// either fails the whole list payload, so one external application blanks the
// page for every application on it.
export const ExternalApplicationSchema = z
  .object({
    id: z.string().uuid(),
    orgId: z.string().min(1),
    title: z.string().min(1).max(120),
    url: ExternalApplicationUrlSchema,
    icon: ApplicationIconKeySchema.optional(),
    createdByUserId: z.string().min(1).nullable(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
  })
  .passthrough();

export const ApplicationsListResponseSchema = z
  .object({
    external: z.array(ExternalApplicationSchema),
    internal: z.array(ApplicationSummarySchema.passthrough()),
    internalAvailability: z.enum(["available", "unavailable"]),
  })
  .passthrough();
export const InternalApplicationResponseSchema = ApplicationDetailSchema.passthrough();
export const ApplicationActionResultSchema = z.object({ result: JsonValueSchema }).passthrough();
export const ApplicationSessionTicketSchema = z
  .object({
    ticket: z.string().min(1),
    socketPath: z.literal(APPLICATION_SOCKET_PATH),
  })
  .strict();

export type ApplicationsListData = z.infer<typeof ApplicationsListResponseSchema>;
export type ExternalApplicationView = z.infer<typeof ExternalApplicationSchema>;
export type InternalApplicationView = z.infer<typeof InternalApplicationResponseSchema>;
export type ApplicationActionResult = z.infer<typeof ApplicationActionResultSchema>;
export type ApplicationSessionTicket = z.infer<typeof ApplicationSessionTicketSchema>;

export function applicationSocketUrl(socketPath: string, pageHref: string): string {
  const url = new URL(socketPath, pageHref);
  if (url.protocol === "https:") url.protocol = "wss:";
  else if (url.protocol === "http:") url.protocol = "ws:";
  else throw new Error("Application socket requires an HTTP(S) Hub origin");
  return url.toString();
}

export type ApplicationsRoute =
  | { kind: "list" }
  | { kind: "internal"; appId: string }
  | { kind: "external"; externalAppId: string }
  | { kind: "invalid" };

export function deriveApplicationsRoute(pathname: string): ApplicationsRoute {
  if (pathname === "/applications" || pathname === "/applications/") return { kind: "list" };
  const segments = pathname.split("/").filter(Boolean);
  if (segments.length !== 3 || segments[0] !== "applications") return { kind: "invalid" };
  let id: string;
  try {
    id = decodeURIComponent(segments[2] ?? "");
  } catch {
    return { kind: "invalid" };
  }
  if (segments[1] === "internal" && /^app_[a-f0-9]{16}$/.test(id)) {
    return { kind: "internal", appId: id };
  }
  if (segments[1] === "external" && z.string().uuid().safeParse(id).success) {
    return { kind: "external", externalAppId: id };
  }
  return { kind: "invalid" };
}

export function buildInternalApplicationSrcdoc(html: string): string {
  return `<!doctype html><html><head><meta charset="utf-8"><meta http-equiv="Content-Security-Policy" content="${INTERNAL_APPLICATION_CSP}"><meta name="viewport" content="width=device-width,initial-scale=1"></head><body>${INTERNAL_APPLICATION_BRIDGE_BOOTSTRAP}${html}</body></html>`;
}

const BridgeDocumentReadySchema = z
  .object({
    type: z.literal("app.document.ready"),
    documentId: z.string().min(1).max(128),
  })
  .strict();
const BridgeDocumentProofSchema = z
  .object({ type: z.literal("app.document.proof"), nonce: z.string().min(1) })
  .strict();

const BridgeRequestSchema = z
  .object({
    type: z.literal("app.action"),
    requestId: z.string().min(1).max(128),
    action: ApplicationActionNameSchema,
    args: JsonValueSchema,
  })
  .strict();
const BridgeRequestEnvelopeSchema = z
  .object({
    type: z.literal("app.action"),
    requestId: z.string().min(1).max(128),
  })
  .passthrough();

export type ApplicationBridgeRequest = z.infer<typeof BridgeRequestSchema>;
export type ApplicationBridgeRequestParseResult =
  | { ok: true; request: ApplicationBridgeRequest }
  | {
      ok: false;
      requestId: string;
      error: {
        code: "INVALID_REQUEST" | "ARGUMENT_TOO_LARGE";
        message: string;
      };
    };

function invalidApplicationBridgeRequest(requestId: string): ApplicationBridgeRequestParseResult {
  return {
    ok: false,
    requestId,
    error: {
      code: "INVALID_REQUEST",
      message: "Application action request is invalid",
    },
  };
}

export function parseApplicationBridgeRequest(
  value: unknown,
): ApplicationBridgeRequestParseResult | undefined {
  let envelope: ReturnType<typeof BridgeRequestEnvelopeSchema.safeParse>;
  try {
    envelope = BridgeRequestEnvelopeSchema.safeParse(value);
  } catch {
    return undefined;
  }
  if (!envelope.success) return undefined;
  let parsed: ReturnType<typeof BridgeRequestSchema.safeParse>;
  try {
    parsed = BridgeRequestSchema.safeParse(value);
  } catch {
    return invalidApplicationBridgeRequest(envelope.data.requestId);
  }
  if (!parsed.success) return invalidApplicationBridgeRequest(envelope.data.requestId);
  let serializedArgs: string;
  try {
    serializedArgs = JSON.stringify(parsed.data.args);
  } catch {
    return invalidApplicationBridgeRequest(envelope.data.requestId);
  }
  if (new TextEncoder().encode(serializedArgs).byteLength > APPLICATION_ARGUMENT_MAX_BYTES) {
    return {
      ok: false,
      requestId: envelope.data.requestId,
      error: {
        code: "ARGUMENT_TOO_LARGE",
        message: `Application action arguments exceed ${APPLICATION_ARGUMENT_MAX_BYTES} UTF-8 bytes`,
      },
    };
  }
  return { ok: true, request: parsed.data };
}

export function requestFromMountedApplicationFrame(
  event: Pick<MessageEvent, "data" | "source">,
  frameWindow: Window | null,
): ApplicationBridgeRequestParseResult | undefined {
  if (!frameWindow || event.source !== frameWindow) return undefined;
  return parseApplicationBridgeRequest(event.data);
}

export function documentReadyFromMountedApplicationFrame(
  event: Pick<MessageEvent, "data" | "ports" | "source">,
  frameWindow: Window | null,
): { documentId: string; port: MessagePort } | undefined {
  if (!frameWindow || event.source !== frameWindow || event.ports.length !== 1) {
    return undefined;
  }
  const parsed = BridgeDocumentReadySchema.safeParse(event.data);
  if (!parsed.success) return undefined;
  return { documentId: parsed.data.documentId, port: event.ports[0] };
}

export function proveApplicationFrameDocument(
  port: MessagePort,
  nonce: string,
  timeoutMs = APPLICATION_DOCUMENT_PROOF_TIMEOUT_MS,
): Promise<boolean> {
  return new Promise((resolve) => {
    let settled = false;
    const finish = (proved: boolean) => {
      if (settled) return;
      settled = true;
      clearTimeout(timeout);
      port.removeEventListener("message", onMessage);
      resolve(proved);
    };
    const onMessage = (event: MessageEvent) => {
      const parsed = BridgeDocumentProofSchema.safeParse(event.data);
      if (parsed.success && parsed.data.nonce === nonce) finish(true);
    };
    const timeout = setTimeout(() => finish(false), timeoutMs);
    port.addEventListener("message", onMessage);
    port.start();
    try {
      port.postMessage({ type: "app.document.challenge", nonce });
    } catch {
      finish(false);
    }
  });
}

export function releaseApplicationFrameDocument(port: MessagePort): void {
  try {
    port.postMessage({ type: "app.document.release" });
  } catch {}
  port.close();
}

export function isCurrentApplicationFrameDocument(
  expectedWindow: Window | null,
  mountedWindow: Window | null,
  expectedDocumentVersion: number,
  mountedDocumentVersion: number,
): boolean {
  return (
    expectedWindow !== null &&
    expectedWindow === mountedWindow &&
    expectedDocumentVersion === mountedDocumentVersion
  );
}
